import React, { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowRight, Play, Shield, Zap, Brain } from "lucide-react";
import { Button } from "./ui/button";
import BookDemo from "./BookDemo";
import FloatingAICards from "./FloatingAICards";
import ClientLogoMarquee from "./ClientLogoMarquee";

const Hero = () => {
  const ref = useRef(null);
  const containerRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.3]);

  return (
    <motion.section
      id="top"
      ref={containerRef}
      className="relative pt-0 pb-0 flex flex-col overflow-hidden min-h-screen"
      style={{
        background:
          "linear-gradient(180deg, #0A0A23 0%, #1A1A3A 50%, #0F0F2B 100%)",
      }}
    >
      {/* Parallax Background Elements */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background: `
              radial-gradient(circle at 20% 30%, rgba(120, 119, 198, 0.08) 0%, transparent 40%),
              radial-gradient(circle at 80% 70%, rgba(255, 119, 198, 0.06) 0%, transparent 40%)
            `,
            pointerEvents: "none",
          }}
        ></div>

        {/* Parallax Background Circles */}
        <motion.div
          style={{ y: y1 }}
          className="absolute top-1/4 left-1/4 w-32 sm:w-48 md:w-96 h-32 sm:h-48 md:h-96 bg-purple-500/6 rounded-full blur-3xl"
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        ></motion.div>
        <motion.div
          style={{ y: y2 }}
          className="absolute bottom-1/4 right-1/4 w-32 sm:w-48 md:w-96 h-32 sm:h-48 md:h-96 bg-pink-500/6 rounded-full blur-3xl"
          animate={{
            opacity: [0.3, 0.4, 0.3],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 6,
          }}
        ></motion.div>
        <motion.div
          style={{ y: y3 }}
          className="absolute top-1/2 right-1/3 w-24 sm:w-32 md:w-64 h-24 sm:h-32 md:h-64 bg-blue-500/4 rounded-full blur-3xl"
          animate={{
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3,
          }}
        ></motion.div>
      </div>

      {/* <FloatingAICards
        containerRef={ref}
        cards={[
          {
            id: 5,
            icon: Shield,
            title: "SOC 2 Compliance",
            description: "71.01% readiness with AI-powered validation",
            color: "blue",
            position: "bottom-[5%] right-8",
          },
        ]}
      /> */}

      <div
        ref={ref}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-7 w-full sm:top-12 md:top-32 pt-20 sm:pt-8 pb-2 sm:pb-16 md:pb-20 flex items-center"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-16 items-center w-full">
          {/* Left Content */}
          <div className="text-left">
            {/* Badge */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-4 sm:mb-6 md:mb-8 backdrop-blur-sm"
            >
              <Shield className="w-3 sm:w-4 h-3 sm:h-4 mr-2 text-purple-400" />
              <span className="text-xs sm:text-sm text-white/80">
                AI-First GRC Platform
              </span>
            </motion.div> */}

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
              className="text-3xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-2 sm:mb-6 leading-tight"
            >
              Compliance that
              <br />
              <span className="text-3xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                {" "}
                Learns
              </span>
              <br />
              <span className="text-3xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl">
                Security That Leads.
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              className="text-sm sm:text-base md:text-lg lg:text-xl text-white/70 mb-4 sm:mb-8 leading-relaxed"
            >
              Regodit gets you compliant faster and smarter by adapting to your
              unique business and regulatory needs, not forcing rigid
              checklists. Security stays strong, and compliance becomes
              continuous.
            </motion.p>

            {/* Feature Pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
              className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-8"
            >
              {[
                { icon: Zap, text: "AI Evidence Collection", highlight: true },
                { icon: Brain, text: "Smart Risk Analysis", highlight: true },
                { icon: Shield, text: "Audit-Ready Reports", highlight: true },
              ].map((feature, index) => (
                <motion.div
                  key={feature.text}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{
                    duration: 0.6,
                    delay: 0.8 + index * 0.1,
                    ease: "easeOut",
                  }}
                  className={`flex items-center px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-full backdrop-blur-sm ${
                    feature.highlight
                      ? "bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30"
                      : "bg-white/5 border border-white/10"
                  }`}
                >
                  <feature.icon
                    className={`w-3 sm:w-4 h-3 sm:h-4 mr-1.5 sm:mr-2 ${
                      feature.highlight ? "text-purple-300" : "text-purple-400"
                    }`}
                  />
                  <span
                    className={`text-xs sm:text-sm ${
                      feature.highlight ? "text-white" : "text-white/80"
                    }`}
                  >
                    {feature.text}
                  </span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.0, ease: "easeOut" }}
              className="flex gap-2 sm:gap-3 md:gap-4 mb-8 w-full"
            >
              {/* <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1"
              >
                <Button
                  onClick={() => (window.location.href = "#testimonials")}
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 text-center justify-center items-center flex w-full"
                >
                  <span>Testimonials</span>
                  <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4 ml-1 sm:ml-2" />
                </Button>
              </motion.div> */}
              <div className="flex-1">
                <BookDemo
                  variant="default"
                  className="border-white/20 text-white hover:bg-white/10 backdrop-blur-sm text-center justify-center items-center flex w-full"
                />
              </div>
            </motion.div>
          </div>

          {/* Right Content - Dashboard */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-3xl blur-3xl"></div>
            <motion.div
              className="relative bg-gradient-to-br from-white/8 to-white/4 rounded-2xl sm:rounded-3xl p-2 sm:p-3 border border-white/20 backdrop-blur-sm shadow-2xl"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
            >
              {/* Dashboard Header */}
              <div className="bg-white/5 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-3 sm:mb-4 border border-white/10">
                <div className="flex items-center justify-between mb-3 sm:mb-4 md:mb-6">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-5 sm:w-6 md:w-8 h-5 sm:h-6 md:h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-xs sm:text-sm">
                        K
                      </span>
                    </div>
                    <span className="text-white font-semibold text-xs sm:text-sm md:text-base">
                      Dashboard
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <span className="text-white/70 text-xs sm:text-sm">
                      Alex Parker
                    </span>
                    <div className="w-5 sm:w-6 md:w-8 h-5 sm:h-6 md:h-8 bg-white/10 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs sm:text-sm">AP</span>
                    </div>
                  </div>
                </div>

                {/* SOC 2 Card */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 text-white">
                  <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
                    <h3 className="text-xs sm:text-sm md:text-lg font-semibold">
                      SOC 2 Readiness Overview
                    </h3>
                    <Shield className="w-3 sm:w-4 md:w-5 h-3 sm:h-4 md:h-5 text-blue-200" />
                  </div>
                  <div className="text-xl sm:text-2xl md:text-4xl font-bold mb-1 sm:mb-2">
                    71.01
                  </div>
                  <div className="text-blue-100 text-xs sm:text-sm mb-2 sm:mb-3 md:mb-4">
                    Your compliance score out of 100 points
                  </div>
                  <div className="w-full bg-blue-800/50 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
                    <motion.div
                      className="bg-gradient-to-r from-blue-300 to-white h-1.5 sm:h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: "71.01%" }}
                      transition={{ duration: 2, delay: 1, ease: "easeOut" }}
                    />
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm text-blue-100">
                    <span>Current</span>
                    <span>71.01%</span>
                  </div>
                </div>
              </div>

              {/* Control Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                <div className="bg-white/5 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <span className="text-white text-xs sm:text-sm font-medium">
                      Control Summary By Domain
                    </span>
                  </div>
                  <div className="space-y-1.5 sm:space-y-2 md:space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white/80 text-xs">
                          Policy and Procedure
                        </div>
                        <div className="text-white/60 text-xs">28 Controls</div>
                      </div>
                      <div className="text-blue-400 font-semibold text-xs sm:text-sm">
                        84%
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white/80 text-xs">
                          People and Organization
                        </div>
                        <div className="text-white/60 text-xs">13 Controls</div>
                      </div>
                      <div className="text-orange-400 font-semibold text-xs sm:text-sm">
                        53%
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <span className="text-white text-xs sm:text-sm font-medium">
                      Control Summary By Criteria
                    </span>
                  </div>
                  <div className="space-y-1.5 sm:space-y-2 md:space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white/80 text-xs">
                          CC1 - Control Environment
                        </div>
                        <div className="text-white/60 text-xs">18 Controls</div>
                      </div>
                      <div className="text-orange-400 font-semibold text-xs sm:text-sm">
                        50%
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white/80 text-xs">
                          CC2 - Communication
                        </div>
                        <div className="text-white/60 text-xs">8 Controls</div>
                      </div>
                      <div className="text-orange-400 font-semibold text-xs sm:text-sm">
                        47%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Client Logo Marquee */}
      <ClientLogoMarquee />
    </motion.section>
  );
};

export default Hero;
